import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { TradingBot } from './TradingBotsManager';
import { TrendingUp, TrendingDown, Target, AlertTriangle, Trophy, Activity } from 'lucide-react';

interface BotDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bot: TradingBot;
}

export function BotDetailsModal({ isOpen, onClose, bot }: BotDetailsModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-violet-500/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{bot.name}</DialogTitle>
          <p className="text-gray-400">{bot.pair}</p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Performance Stats */}
          <div>
            <h3 className="text-lg text-violet-400 mb-4">Rendimiento</h3>
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-black/40 border-violet-500/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-gray-400">Total Operaciones</span>
                </div>
                <div className="text-2xl text-white">{bot.performance.totalTrades}</div>
              </Card>

              <Card className="bg-black/40 border-violet-500/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-gray-400">Operaciones Ganadoras</span>
                </div>
                <div className="text-2xl text-green-400">{bot.performance.winningTrades}</div>
              </Card>

              <Card className="bg-black/40 border-violet-500/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-gray-400">Operaciones Perdedoras</span>
                </div>
                <div className="text-2xl text-red-400">{bot.performance.losingTrades}</div>
              </Card>

              <Card className="bg-black/40 border-violet-500/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-gray-400">Win Rate</span>
                </div>
                <div className="text-2xl text-white">{bot.winRate.toFixed(1)}%</div>
              </Card>
            </div>
          </div>

          {/* Configuration */}
          <div>
            <h3 className="text-lg text-violet-400 mb-4">Configuración Actual</h3>
            <Card className="bg-black/40 border-violet-500/20 p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-violet-500/10">
                  <span className="text-gray-400">Estrategia</span>
                  <Badge variant="outline" className="border-violet-500/30 text-violet-400">
                    {bot.strategy}
                  </Badge>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-violet-500/10">
                  <span className="text-gray-400">Inversión Inicial</span>
                  <span className="text-white">${bot.investment.toLocaleString()}</span>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-violet-500/10">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-green-400" />
                    <span className="text-gray-400">Umbral de Compra</span>
                  </div>
                  <span className="text-white">{bot.config.buyThreshold}%</span>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-violet-500/10">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    <span className="text-gray-400">Umbral de Venta</span>
                  </div>
                  <span className="text-white">{bot.config.sellThreshold}%</span>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-violet-500/10">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-gray-400">Stop Loss</span>
                  </div>
                  <span className="text-white">{bot.config.stopLoss}%</span>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-violet-500/10">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-green-400" />
                    <span className="text-gray-400">Take Profit</span>
                  </div>
                  <span className="text-white">{bot.config.takeProfit}%</span>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-violet-500/10">
                  <span className="text-gray-400">Monto por Operación</span>
                  <span className="text-white">${bot.config.tradeAmount}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Máx. Operaciones/Día</span>
                  <span className="text-white">{bot.config.maxTradesPerDay}</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Profit Summary */}
          <div>
            <h3 className="text-lg text-violet-400 mb-4">Resumen de Ganancias</h3>
            <Card className="bg-black/40 border-violet-500/20 p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Ganancia Total</span>
                  <span className={`text-xl ${bot.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {bot.profit >= 0 ? '+' : ''}${bot.profit.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Porcentaje</span>
                  <span className={`text-xl ${bot.profitPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {bot.profitPercent >= 0 ? '+' : ''}{bot.profitPercent.toFixed(2)}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Ganancia Promedio por Operación</span>
                  <span className="text-white">
                    ${bot.performance.avgProfit.toFixed(2)}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Timestamps */}
          <div>
            <h3 className="text-lg text-violet-400 mb-4">Información Temporal</h3>
            <Card className="bg-black/40 border-violet-500/20 p-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Creado</span>
                  <span className="text-white">
                    {new Date(bot.createdAt).toLocaleString()}
                  </span>
                </div>
                {bot.lastTrade && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Última Operación</span>
                    <span className="text-white">
                      {new Date(bot.lastTrade).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
